/**
 * AI-bracket auto-detection heuristic for fusionMode='auto'.
 *
 * Codex 2.A.2.design D3+HIGH-1 lock-in. Lives as a SHARED helper because
 * both merge.js (blob/pixel API) and ui/hdr-merge-modal.js (UI flow)
 * dispatch into fuseBrackets independently — a detection function that
 * lived only in merge.js would be bypassed by the UI path. Both paths
 * must call detectFusionMode() and pass the resolved mode forward.
 *
 * Three signals, each contributes +1 evidence. ≥2 signals → robust mode.
 * Conservative — falls back to camera when evidence is insufficient or
 * inputs make the heuristic unreliable.
 *
 * Signals:
 *   1. Missing absolute EV metadata for ALL brackets (single-bracket
 *      EXIF gaps don't count — only complete failure).
 *   2. Pairwise Pearson correlation on EV-normalized luminance at
 *      pyramid level 2 (downsampled 4×). median(pair_r) < 0.95 → +1.
 *   3. Flat-patch disagreement on EV-normalized data: 16×16 patches
 *      where contrast and saturation are both low; median patch-stddev
 *      across brackets > 0.02 in linear → +1. Requires a minimum
 *      number of valid flat patches; insufficient evidence → no signal.
 *
 * NB: NOT to be confused with backend.js:fuseBracketsAuto which is the
 * GPU/CPU axis. fusionMode='auto' is the camera/robust axis. They are
 * orthogonal and can both be 'auto' simultaneously (Codex HIGH-2).
 */

const PEARSON_THRESHOLD = 0.95;
const PATCH_STDDEV_THRESHOLD = 0.02;
const PATCH_SIZE = 16;
const MIN_VALID_PATCHES = 8;
/* Flat-patch criterion: a patch is "flat" when both contrast (Laplacian)
 * and saturation are below these thresholds — mirrors the regions where
 * AI-bracket noise dominates and where median injection helps. */
const FLAT_CONTRAST_THRESHOLD = 0.01;
const FLAT_SAT_THRESHOLD = 0.05;

/**
 * Detect whether a stack of EV-normalized brackets is camera-like (well-
 * correlated, photometrically consistent) or AI-like (uncorrelated noise,
 * suspect EV metadata).
 *
 * Codex 2.A.2.fix HIGH-1: callers MUST pass `exifMissing` explicitly
 * because they typically substitute estimated EV stops when EXIF is
 * absent — leaving us no way to infer "missing EXIF" from the evStops
 * argument alone (it's always finite by the time it reaches us). The
 * second-arg ergonomic shape `{ evStops, exifMissing }` keeps the signal
 * 1 input distinct from the EV values used for diagnostics. evStops is
 * not actually consumed by detection right now (kept for future use /
 * forward compatibility).
 *
 * @param {Array<{rgba: Float32Array, width: number, height: number}>} stack
 *        EV-normalized stack (after fusePostProcess EV-scale step)
 * @param {{ evStops?: Float32Array | number[] | null,
 *           exifMissing?: boolean } | Float32Array | number[] | null} info
 *        Either a metadata bag (preferred) or — for backward compat —
 *        just the evStops array. When passed as an array (the v1 form),
 *        we infer `exifMissing` from finite-ness, which matches the
 *        previous behaviour but produces false negatives on production
 *        callers that always pass finite stops.
 * @returns {{ mode: 'camera' | 'robust',
 *             signals: { exifMissing: boolean, pearson: number, patchStddev: number, validPatches: number },
 *             evidence: number }}
 */
export function detectFusionMode(stack, info) {
    if (!Array.isArray(stack) || stack.length < 2) {
        return {
            mode: 'camera',
            signals: { exifMissing: false, pearson: 1, patchStddev: 0, validPatches: 0 },
            evidence: 0,
        };
    }

    const { width, height } = stack[0];
    const N = stack.length;

    /* Resolve info bag vs legacy array argument. */
    const isBag = info != null
        && typeof info === 'object'
        && !Array.isArray(info)
        && !ArrayBuffer.isView(info);
    const evStops = isBag ? info.evStops : info;
    const explicitExifMissing = isBag && typeof info.exifMissing === 'boolean'
        ? info.exifMissing
        : null;

    /* Signal 1: missing EV metadata. If the caller gave us an explicit
     * `exifMissing` flag, trust it (Codex HIGH-1: production callers
     * substitute estimated EV stops as a fallback, so finite-ness of
     * evStops alone is a false-negative trap). Else fall back to the
     * legacy inference for unit tests / callers that haven't migrated. */
    const exifMissing = explicitExifMissing != null
        ? explicitExifMissing
        : (!evStops
            || evStops.length !== N
            || !Array.from(evStops).every(Number.isFinite));

    /* Signal 2: pairwise Pearson on EV-normalized luminance pyramid level 2.
     *
     * Build luminance maps once, then 4× block-average to level 2 (cheap
     * proxy for "real" Gaussian pyramid downsample — exact match doesn't
     * matter for a heuristic). */
    const lumLvl0 = stack.map(b => luminanceMap(b.rgba, width, height));
    const lvl2W = Math.max(1, width  >> 2);
    const lvl2H = Math.max(1, height >> 2);
    const lumLvl2 = lumLvl0.map(lum => block4xMean(lum, width, height, lvl2W, lvl2H));

    const pairwiseR = [];
    for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
            pairwiseR.push(pearson(lumLvl2[i], lumLvl2[j]));
        }
    }
    const medianR = median(pairwiseR);

    /* Signal 3: flat-patch stddev on EV-normalized luminance, full-res.
     *
     * For each non-overlapping 16×16 patch of the FIRST bracket, measure:
     *   - contrast = max-min of the patch luminance (cheap variance proxy)
     *   - saturation = average across-channel stddev
     * If both are below thresholds, the patch is "flat". For each flat
     * patch, compute the stddev of mean luminance across brackets. */
    const { medianStddev, validCount } = flatPatchStddev(stack, lumLvl0);

    /* Tally evidence — ≥2 → robust. */
    let evidence = 0;
    if (exifMissing) evidence += 1;
    if (medianR < PEARSON_THRESHOLD) evidence += 1;
    if (validCount >= MIN_VALID_PATCHES && medianStddev > PATCH_STDDEV_THRESHOLD) evidence += 1;

    return {
        mode: evidence >= 2 ? 'robust' : 'camera',
        signals: {
            exifMissing,
            pearson: medianR,
            patchStddev: medianStddev,
            validPatches: validCount,
        },
        evidence,
    };
}

/* ── Helpers (exported for tests) ────────────────────────────────────── */

/** Rec.709 luminance map of an RGBA Float32 buffer. */
export function luminanceMap(rgba, width, height) {
    const N = width * height;
    const out = new Float32Array(N);
    for (let i = 0, p = 0; i < N; i++, p += 4) {
        out[i] = 0.2126 * rgba[p] + 0.7152 * rgba[p + 1] + 0.0722 * rgba[p + 2];
    }
    return out;
}

/**
 * 4× block-average downsample. dstW/dstH should already be set to
 * floor(srcW/4) / floor(srcH/4) (clamped to ≥1 by caller).
 */
export function block4xMean(src, srcW, srcH, dstW, dstH) {
    const out = new Float32Array(dstW * dstH);
    for (let dy = 0; dy < dstH; dy++) {
        for (let dx = 0; dx < dstW; dx++) {
            let sum = 0, count = 0;
            const ymax = Math.min(srcH, (dy + 1) * 4);
            const xmax = Math.min(srcW, (dx + 1) * 4);
            for (let y = dy * 4; y < ymax; y++) {
                for (let x = dx * 4; x < xmax; x++) {
                    sum += src[y * srcW + x];
                    count += 1;
                }
            }
            out[dy * dstW + dx] = count ? sum / count : 0;
        }
    }
    return out;
}

/** Pearson correlation of two same-length vectors. Returns 1 on
 * degenerate cases (zero variance) — degenerate ⇒ no info ⇒ no signal. */
export function pearson(a, b) {
    if (a.length !== b.length || a.length === 0) return 1;
    const n = a.length;
    let sumA = 0, sumB = 0;
    for (let i = 0; i < n; i++) { sumA += a[i]; sumB += b[i]; }
    const mA = sumA / n, mB = sumB / n;
    let num = 0, dA = 0, dB = 0;
    for (let i = 0; i < n; i++) {
        const da = a[i] - mA;
        const db = b[i] - mB;
        num += da * db; dA += da * da; dB += db * db;
    }
    const denom = Math.sqrt(dA * dB);
    return denom > 0 ? num / denom : 1;
}

function median(arr) {
    if (arr.length === 0) return 0;
    const s = Array.from(arr).sort((x, y) => x - y);
    const mid = (s.length - 1) >> 1;          /* lower-median, even count */
    return s[mid];
}

/**
 * Per-patch flat-region stddev across brackets. Returns the median patch
 * stddev plus the count of valid flat patches found.
 */
function flatPatchStddev(stack, lumStack) {
    const { width, height } = stack[0];
    const N = stack.length;
    const ph = Math.floor(height / PATCH_SIZE);
    const pw = Math.floor(width  / PATCH_SIZE);
    if (pw === 0 || ph === 0) {
        return { medianStddev: 0, validCount: 0 };
    }

    const patchStddevs = [];
    /* Use first bracket's structure to decide flatness — same pixels
     * are evaluated for stddev across all brackets at the same patch. */
    const firstRgba = stack[0].rgba;
    const firstLum = lumStack[0];

    for (let py = 0; py < ph; py++) {
        for (let px = 0; px < pw; px++) {
            const y0 = py * PATCH_SIZE;
            const x0 = px * PATCH_SIZE;
            /* Quick flatness check on first bracket. */
            let lmin = Infinity, lmax = -Infinity;
            let satSum = 0;
            for (let y = y0; y < y0 + PATCH_SIZE; y++) {
                for (let x = x0; x < x0 + PATCH_SIZE; x++) {
                    const lv = firstLum[y * width + x];
                    if (lv < lmin) lmin = lv;
                    if (lv > lmax) lmax = lv;
                    const p4 = (y * width + x) * 4;
                    const r = firstRgba[p4], g = firstRgba[p4 + 1], b = firstRgba[p4 + 2];
                    const m = (r + g + b) / 3;
                    const dr = r - m, dg = g - m, db = b - m;
                    satSum += Math.sqrt((dr * dr + dg * dg + db * db) / 3);
                }
            }
            const contrast = lmax - lmin;
            const meanSat = satSum / (PATCH_SIZE * PATCH_SIZE);
            if (contrast >= FLAT_CONTRAST_THRESHOLD) continue;
            if (meanSat >= FLAT_SAT_THRESHOLD) continue;

            /* Patch is flat: compute mean luminance per bracket inside,
             * then stddev of those means across brackets. */
            const means = new Array(N);
            for (let k = 0; k < N; k++) {
                let sum = 0;
                for (let y = y0; y < y0 + PATCH_SIZE; y++) {
                    for (let x = x0; x < x0 + PATCH_SIZE; x++) {
                        sum += lumStack[k][y * width + x];
                    }
                }
                means[k] = sum / (PATCH_SIZE * PATCH_SIZE);
            }
            let mAll = 0;
            for (let k = 0; k < N; k++) mAll += means[k];
            mAll /= N;
            let varK = 0;
            for (let k = 0; k < N; k++) {
                const d = means[k] - mAll;
                varK += d * d;
            }
            patchStddevs.push(Math.sqrt(varK / N));
        }
    }

    return {
        medianStddev: median(patchStddevs),
        validCount: patchStddevs.length,
    };
}

/* ── Constants (exported for tests + UI thresholds) ──────────────────── */
export const AUTO_DETECT_THRESHOLDS = Object.freeze({
    PEARSON: PEARSON_THRESHOLD,
    PATCH_STDDEV: PATCH_STDDEV_THRESHOLD,
    PATCH_SIZE,
    MIN_VALID_PATCHES,
    FLAT_CONTRAST: FLAT_CONTRAST_THRESHOLD,
    FLAT_SAT: FLAT_SAT_THRESHOLD,
});
